import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ProjectService } from '../../../services/project.service';
import { ReportJobsService } from '../../../services/report-jobs.service';
import { ReportHubService } from '../../../services/report-hub.service';
import { ToastService } from '../../../services/toast.service';

interface DropdownOption {
  label: string;
  value: string | number;
}

@Component({
  selector: 'app-project-program-detail-reports',
  templateUrl: './project-program-detail-reports.component.html',
  styleUrls: ['./project-program-detail-reports.component.scss']
})
export class ProjectProgramDetailReportsComponent implements OnInit, OnDestroy {
  reportForm!: FormGroup;
  loading = false;
  projectsDropdown: DropdownOption[] = [];

  private reportHubSub!: Subscription;
  private pendingJobId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private _projectService: ProjectService,
    private _reportJobsService: ReportJobsService,
    private _reportHubService: ReportHubService,
    private _toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadProjects();

    this.reportHubSub = this._reportHubService.reportStatusChanged$.subscribe(notification => {
      if (notification.jobId !== this.pendingJobId) return;

      if (notification.status === 'Completed') {
        this.loading = false;
        this._toastService.showToast('success', 'Reporte de detalle programa generado exitosamente. Descargando...');
        this._reportJobsService.downloadReport(notification.jobId, notification.fileName || undefined);
        this.pendingJobId = null;
      } else if (notification.status === 'Failed') {
        this.loading = false;
        this._toastService.showToast('error', `Error al generar el reporte: ${notification.errorMessage || 'Desconocido'}`);
        this.pendingJobId = null;
      } else if (notification.status === 'Processing') {
        this._toastService.showToast('info', 'Procesando reporte de detalle programa...');
      }
    });
  }

  ngOnDestroy(): void {
    this.reportHubSub?.unsubscribe();
  }

  private initForm(): void {
    this.reportForm = this.fb.group({
      projectId: [null, Validators.required]
    });
  }

  // Helper para extraer datos de estructura paginada o array directo
  private extractData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  private loadProjects(): void {
    this._projectService.getAll().subscribe({
      next: (resp) => {
        const data = this.extractData(resp);
        this.projectsDropdown = data.map((p: any) => ({
          label: `${p.projectId} - ${p.name}`,
          value: p.id
        }));
      },
      error: () => this.projectsDropdown = []
    });
  }

  downloadExcelReport(): void {
    if (this.reportForm.invalid) {
      this.reportForm.markAllAsTouched();
      return;
    }
    const { projectId } = this.reportForm.value;
    this.loading = true;
    this._reportJobsService.queueProjectProgramDetailReport(projectId).subscribe({
      next: (response) => {
        this.pendingJobId = response.jobId;
        this._toastService.showToast('info', 'Generando reporte de detalle programa en segundo plano...');
      },
      error: (error: any) => {
        console.error('Error al solicitar el reporte de programa detallado:', error);
        this._toastService.showToast('error', 'Error al solicitar el reporte');
        this.loading = false;
      }
    });
  }

  // Helpers para UI estilo banks-reports
  get selectedProjectId(): number | null {
    return this.reportForm.get('projectId')?.value || null;
  }

  get selectedProjectName(): string {
    const p = this.projectsDropdown.find(pr => pr.value === this.selectedProjectId);
    return p ? p.label : 'Sin seleccionar';
  }
}
